/**
 * Design: Swiss Precision × 和の余白
 * 効果シミュレーター — 月間件数・作業時間・人件費を入力して
 * Quick Win / Standard Ops / Scale & Control の費用対効果を試算
 * Mobile: スタック型レイアウト、スライダー操作、カード横スクロール
 */

import { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Calculator,
  TrendingUp,
  Clock,
  DollarSign,
  Zap,
  Settings,
  Expand,
  ArrowRight,
  Info,
  RotateCcw,
} from "lucide-react";
import { scrollToContactForm } from "@/hooks/useScrollToContact";

/* ─── Constants ─── */

const SETS = [
  {
    name: "Quick Win",
    setId: "quick",
    items: 3,
    rate: 0.4,
    label: "現場が「回る」最低限",
    period: "2〜4週間",
    color: "#E03030",
    icon: Zap,
    description: "メール自動起票＋即時通知＋例外キューの3アイテムで、最短2週間で効果を体感",
  },
  {
    name: "Standard Ops",
    setId: "standard",
    items: 5,
    rate: 0.6,
    label: "現場が「回り続ける」運用まで",
    period: "1〜2ヶ月",
    color: "#2563EB",
    icon: Settings,
    description: "Quick Win＋ファイル整流化＋KPI自動集計で、運用が自走する状態を構築",
  },
  {
    name: "Scale & Control",
    setId: "scale",
    items: 7,
    rate: 0.75,
    label: "会社が「増やせる」統制まで",
    period: "2〜3ヶ月",
    color: "#1A1A1A",
    icon: Expand,
    description: "Standard Ops＋自動チェック＋監査ログパックで、横展開可能な基盤を整備",
  },
] as const;

const DEFAULTS = { n: 200, t: 15, c: 3000 };
const PRESETS = [
  { label: "製造業（受発注）", n: 300, t: 20, c: 2800 },
  { label: "卸売（請求処理）", n: 500, t: 10, c: 2500 },
  { label: "サービス業（予約管理）", n: 150, t: 25, c: 3200 },
  { label: "物流（出荷管理）", n: 400, t: 12, c: 2600 },
];

/* ─── Helpers ─── */

function formatNumber(n: number): string {
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}千万`;
  if (n >= 10000) return `${Math.round(n / 10000).toLocaleString()}万`;
  return n.toLocaleString();
}

function formatHours(h: number): string {
  if (h >= 100) return `${Math.round(h).toLocaleString()}`;
  return h.toFixed(1);
}

/* ─── Slider Input Component ─── */

function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  unit,
  icon: Icon,
  description,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  unit: string;
  icon: React.ElementType;
  description: string;
}) {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="space-y-2 sm:space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#F9F9F9] flex items-center justify-center">
            <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#E03030]" />
          </div>
          <div>
            <p className="text-sm sm:text-[15px] font-semibold text-[#1A1A1A]">{label}</p>
            <p className="text-[11px] sm:text-xs text-[#999]">{description}</p>
          </div>
        </div>
        <div className="flex items-baseline gap-1 shrink-0 ml-2">
          <input
            type="number"
            value={value}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!isNaN(v) && v >= min && v <= max) onChange(v);
            }}
            className="w-16 sm:w-20 text-right text-lg sm:text-xl font-bold text-[#1A1A1A] bg-transparent border-b-2 border-[#E03030]/30 focus:border-[#E03030] outline-none transition-colors tabular-nums"
            min={min}
            max={max}
            step={step}
          />
          <span className="text-xs sm:text-sm text-[#666]">{unit}</span>
        </div>
      </div>
      <div className="relative h-2 sm:h-[10px] rounded-full bg-[#F0F0F0] overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#E03030] to-[#E03030]/70 transition-all duration-200"
          style={{ width: `${percentage}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label={label}
        />
      </div>
      <div className="flex justify-between text-[10px] sm:text-[11px] text-[#BBB]">
        <span>{min.toLocaleString()}</span>
        <span>{max.toLocaleString()}</span>
      </div>
    </div>
  );
}

/* ─── Result Card Component ─── */

function ResultCard({
  set,
  currentHours,
  index,
}: {
  set: (typeof SETS)[number];
  currentHours: number;
  index: number;
}) {
  const Icon = set.icon;
  const savedHours = currentHours * set.rate;
  const remainHours = currentHours - savedHours;

  // We need costPerHour from parent, pass via context or prop
  // Actually let's compute from parent values
  return null; // placeholder, actual rendering below
}

/* ─── Main Component ─── */

export function SimulatorSection() {
  const [n, setN] = useState(DEFAULTS.n);
  const [t, setT] = useState(DEFAULTS.t);
  const [c, setC] = useState(DEFAULTS.c);
  const [activePreset, setActivePreset] = useState<number | null>(null);

  const handleReset = useCallback(() => {
    setN(DEFAULTS.n);
    setT(DEFAULTS.t);
    setC(DEFAULTS.c);
    setActivePreset(null);
  }, []);

  const handlePreset = useCallback((index: number) => {
    const p = PRESETS[index];
    setN(p.n);
    setT(p.t);
    setC(p.c);
    setActivePreset(index);
  }, []);

  const results = useMemo(() => {
    const currentHoursMonth = (n * t) / 60;
    const currentCostMonth = currentHoursMonth * c;

    return SETS.map((set) => {
      const savedHours = currentHoursMonth * set.rate;
      const savedCost = savedHours * c;
      const savedCostYear = savedCost * 12;
      const remainHours = currentHoursMonth - savedHours;

      return {
        ...set,
        currentHoursMonth,
        currentCostMonth,
        savedHours,
        savedCost,
        savedCostYear,
        remainHours,
        barPercent: set.rate * 100,
      };
    });
  }, [n, t, c]);

  const currentHoursMonth = (n * t) / 60;
  const currentCostMonth = currentHoursMonth * c;

  return (
    <section
      id="simulator"
      className="py-16 sm:py-20 md:py-28 bg-white"
      aria-labelledby="simulator-heading"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="mb-10 sm:mb-14 md:mb-16">
          <div className="flex items-center gap-2 mb-4 sm:mb-5">
            <div className="w-1 h-5 sm:h-6 bg-[#E03030] rounded-full" />
            <span className="text-xs sm:text-sm font-semibold tracking-[0.15em] text-[#999] uppercase">
              ROI Simulator
            </span>
          </div>
          <h2
            id="simulator-heading"
            className="text-2xl sm:text-3xl md:text-[2.5rem] font-bold text-[#1A1A1A] leading-tight mb-3 sm:mb-4"
          >
            効果シミュレーター
          </h2>
          <p className="text-sm sm:text-[15px] md:text-base text-[#666] leading-relaxed max-w-2xl">
            月間の業務件数・1件あたりの作業時間・人件費を入力するだけで、
            各セット適用後の<strong className="text-[#1A1A1A]">削減工数</strong>と
            <strong className="text-[#1A1A1A]">金額効果</strong>を即座に試算できます。
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-[400px_1fr] gap-6 lg:gap-10 items-start">
          {/* Left: Input Panel */}
          <div className="lg:sticky lg:top-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-[#E8E8E8] rounded-2xl p-5 sm:p-7 shadow-[0_2px_20px_rgba(0,0,0,0.04)]"
            >
              <div className="flex items-center justify-between mb-6 sm:mb-7">
                <div className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-[#E03030]" />
                  <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A]">入力条件</h3>
                </div>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1 text-xs text-[#999] hover:text-[#E03030] transition-colors"
                  aria-label="入力をリセット"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  リセット
                </button>
              </div>

              {/* Preset Buttons */}
              <div className="mb-6 sm:mb-7">
                <p className="text-[11px] sm:text-xs text-[#999] mb-2 sm:mb-3">業種プリセット</p>
                <div className="grid grid-cols-2 gap-2">
                  {PRESETS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => handlePreset(i)}
                      className={`text-[11px] sm:text-xs py-2 px-3 rounded-lg border transition-all duration-200 ${
                        activePreset === i
                          ? "border-[#E03030] bg-[#E03030]/5 text-[#E03030] font-semibold"
                          : "border-[#E8E8E8] text-[#666] hover:border-[#CCC] hover:bg-[#FAFAFA]"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Slider Inputs */}
              <div className="space-y-6 sm:space-y-7">
                <SliderInput
                  label="月間件数"
                  value={n}
                  onChange={(v) => { setN(v); setActivePreset(null); }}
                  min={10}
                  max={2000}
                  step={10}
                  unit="件/月"
                  icon={TrendingUp}
                  description="対象業務の月間処理件数"
                />
                <SliderInput
                  label="1件あたり作業時間"
                  value={t}
                  onChange={(v) => { setT(v); setActivePreset(null); }}
                  min={1}
                  max={120}
                  step={1}
                  unit="分/件"
                  icon={Clock}
                  description="手作業での平均所要時間"
                />
                <SliderInput
                  label="人件費単価"
                  value={c}
                  onChange={(v) => { setC(v); setActivePreset(null); }}
                  min={1000}
                  max={8000}
                  step={100}
                  unit="円/時"
                  icon={DollarSign}
                  description="時給換算の人件費"
                />
              </div>

              {/* Current Status Summary */}
              <div className="mt-6 sm:mt-7 pt-5 sm:pt-6 border-t border-[#F0F0F0]">
                <p className="text-[11px] sm:text-xs text-[#999] mb-2">現状の業務コスト</p>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] tabular-nums">
                      {formatHours(currentHoursMonth)}
                      <span className="text-sm sm:text-base font-normal text-[#666] ml-1">h/月</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg sm:text-xl font-bold text-[#1A1A1A] tabular-nums">
                      ¥{formatNumber(currentCostMonth)}
                      <span className="text-xs sm:text-sm font-normal text-[#666]">/月</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Results Panel */}
          <div className="space-y-4 sm:space-y-5">
            {results.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="bg-white border border-[#E8E8E8] rounded-2xl overflow-hidden shadow-[0_2px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_30px_rgba(0,0,0,0.08)] transition-shadow duration-300"
                >
                  {/* Card Header */}
                  <div
                    className="px-5 sm:px-7 py-4 sm:py-5 flex items-center justify-between"
                    style={{ borderBottom: `2px solid ${r.color}15` }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${r.color}10` }}
                      >
                        <Icon className="w-5 h-5 sm:w-[22px] sm:h-[22px]" style={{ color: r.color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: r.color }}
                          >
                            {r.items}アイテム
                          </span>
                          <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A]">{r.name}</h3>
                        </div>
                        <p className="text-[11px] sm:text-xs text-[#999] mt-0.5">{r.label}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-[11px] sm:text-xs text-[#999]">実装期間</p>
                      <p className="text-sm sm:text-[15px] font-semibold text-[#1A1A1A]">{r.period}</p>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="px-5 sm:px-7 py-5 sm:py-6">
                    {/* Progress Bar */}
                    <div className="mb-5 sm:mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] sm:text-xs text-[#999]">削減率</span>
                        <span
                          className="text-lg sm:text-xl font-bold tabular-nums"
                          style={{ color: r.color }}
                        >
                          {Math.round(r.rate * 100)}%
                        </span>
                      </div>
                      <div className="h-3 sm:h-4 rounded-full bg-[#F0F0F0] overflow-hidden relative">
                        <motion.div
                          className="absolute inset-y-0 left-0 rounded-full"
                          style={{ backgroundColor: r.color }}
                          initial={{ width: 0 }}
                          whileInView={{ width: `${r.barPercent}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.8, delay: 0.2 + i * 0.1, ease: "easeOut" }}
                        />
                        {/* Remaining portion label */}
                        <div
                          className="absolute inset-y-0 flex items-center text-[9px] sm:text-[10px] font-medium text-[#999]"
                          style={{ left: `${r.barPercent + 2}%` }}
                        >
                          残 {Math.round((1 - r.rate) * 100)}%
                        </div>
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                      <div className="bg-[#FAFAFA] rounded-xl p-3 sm:p-4">
                        <p className="text-[10px] sm:text-[11px] text-[#999] mb-1">削減工数</p>
                        <p className="text-lg sm:text-xl font-bold text-[#1A1A1A] tabular-nums">
                          {formatHours(r.savedHours)}
                          <span className="text-[10px] sm:text-xs font-normal text-[#999] ml-0.5">h/月</span>
                        </p>
                      </div>
                      <div className="bg-[#FAFAFA] rounded-xl p-3 sm:p-4">
                        <p className="text-[10px] sm:text-[11px] text-[#999] mb-1">残り工数</p>
                        <p className="text-lg sm:text-xl font-bold text-[#666] tabular-nums">
                          {formatHours(r.remainHours)}
                          <span className="text-[10px] sm:text-xs font-normal text-[#999] ml-0.5">h/月</span>
                        </p>
                      </div>
                      <div
                        className="rounded-xl p-3 sm:p-4"
                        style={{ backgroundColor: `${r.color}08` }}
                      >
                        <p className="text-[10px] sm:text-[11px] text-[#999] mb-1">月間効果</p>
                        <p className="text-lg sm:text-xl font-bold tabular-nums" style={{ color: r.color }}>
                          ¥{formatNumber(r.savedCost)}
                        </p>
                      </div>
                      <div
                        className="rounded-xl p-3 sm:p-4 relative overflow-hidden"
                        style={{ backgroundColor: `${r.color}12` }}
                      >
                        <p className="text-[10px] sm:text-[11px] text-[#999] mb-1">年間効果</p>
                        <p className="text-lg sm:text-xl font-bold tabular-nums" style={{ color: r.color }}>
                          ¥{formatNumber(r.savedCostYear)}
                        </p>
                        <div
                          className="absolute -right-2 -bottom-2 w-12 h-12 rounded-full opacity-10"
                          style={{ backgroundColor: r.color }}
                        />
                      </div>
                    </div>

                    {/* Description + CTA */}
                    <div className="mt-4 sm:mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <p className="text-[11px] sm:text-xs text-[#999] leading-relaxed flex-1">
                        {r.description}
                      </p>
                      <button
                        onClick={() => scrollToContactForm({ set: r.setId })}
                        className="inline-flex items-center justify-center gap-1.5 text-xs sm:text-sm font-semibold py-2.5 px-5 rounded-lg transition-all duration-200 shrink-0"
                        style={{
                          backgroundColor: r.color,
                          color: "#FFFFFF",
                        }}
                      >
                        このセットで相談
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Disclaimer */}
            <div className="flex items-start gap-2 px-1 pt-2">
              <Info className="w-3.5 h-3.5 text-[#BBB] shrink-0 mt-0.5" />
              <p className="text-[10px] sm:text-[11px] text-[#BBB] leading-relaxed">
                ※ 削減率は仮置き値です。PoC期間中に実際の業務3件を計測し、実測値に置き換えます。
                計算式：現状工数 = 月件数 × 作業時間 ÷ 60、金額効果 = 削減工数 × 人件費単価
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
